import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { LeagueSettings } from '../types/trade';

const defaultSettings: LeagueSettings = {
  teamCount: 12,
  superflex: false,
  scoring: {
    PPR: true,
    pointsPerReception: 1.0,
    pointsPerPassTD: 4.0,
    pointsPerCarry: 0.0,
    pointsPerRushTD: 6.0,
    pointsPerRushYard: 0.1,
    pointsPerRecTD: 6.0,
    pointsPerRecYard: 0.1,
    pointsPerPassYard: 0.04,
    pointsPerPassINT: -2.0,
    pointsPerFumble: -2.0,
    tePremiumPPR: 0.0,
  },
  roster: {
    QB: 1,
    RB: 2,
    WR: 2,
    TE: 1,
    FLEX: 1,
    SUPERFLEX: 0,
    K: 1,
    DEF: 1,
  },
  consistencyWeight: 0.3,
};

interface LeagueData {
  settings: LeagueSettings;
  leagueName?: string;
  importedFrom?: 'sleeper' | 'manual' | null;
  lastUpdated?: string;
}

interface ContextValue {
  leagueData: LeagueData;
  setLeagueData: React.Dispatch<React.SetStateAction<LeagueData>>;
  updateFromSleeper: (settings: LeagueSettings, leagueName: string) => void;
  resetToDefault: () => void;
  hasImportedSettings: boolean;
}

const LeagueSettingsContext = createContext<ContextValue | undefined>(undefined);

export const LeagueSettingsProvider = ({ children }: { children: ReactNode }) => {
  const [leagueData, setLeagueData] = useState<LeagueData>(() => {
    const stored = localStorage.getItem('leagueData');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Ensure all required fields exist for backwards compatibility
        return {
          settings: { ...defaultSettings, ...parsed.settings },
          leagueName: parsed.leagueName,
          importedFrom: parsed.importedFrom || null,
          lastUpdated: parsed.lastUpdated,
        };
      } catch {
        return {
          settings: defaultSettings,
          importedFrom: null,
        };
      }
    }
    return {
      settings: defaultSettings,
      importedFrom: null,
    };
  });

  // Save to localStorage whenever leagueData changes
  useEffect(() => {
    localStorage.setItem('leagueData', JSON.stringify(leagueData));
  }, [leagueData]);

  const updateFromSleeper = (settings: LeagueSettings, leagueName: string) => {
    setLeagueData({
      settings,
      leagueName,
      importedFrom: 'sleeper',
      lastUpdated: new Date().toISOString(),
    });
  };

  const resetToDefault = () => {
    setLeagueData({
      settings: defaultSettings,
      importedFrom: null,
    });
    localStorage.removeItem('leagueData');
  };

  const hasImportedSettings = leagueData.importedFrom === 'sleeper';

  return (
    <LeagueSettingsContext.Provider value={{ 
      leagueData, 
      setLeagueData, 
      updateFromSleeper, 
      resetToDefault, 
      hasImportedSettings 
    }}>
      {children}
    </LeagueSettingsContext.Provider>
  );
};

export const useLeagueSettings = (): ContextValue => {
  const ctx = useContext(LeagueSettingsContext);
  if (!ctx) throw new Error('useLeagueSettings must be used within a LeagueSettingsProvider');
  return ctx;
};
